// Assign a plan to ONE known family, from that family's own page.
//
// This is the SAME grant path as the Subscriptions page — it posts to the one
// existing `POST /admin/subscriptions/grant` endpoint via the one existing
// `grantSubscription()` client. There is deliberately no second grant
// mechanism: two ways to give someone access is two ways for them to diverge.
//
// The only difference from the Subscriptions dialog is that the family is
// already known here, so there is no family picker to get wrong — which is the
// whole reason for surfacing it on this page.
//
// 🔴 A COMP IS AN ORDINARY SUBSCRIPTION. The API writes an `active` row with
// `metadata.comp`, `granted_by_user_id` and no `provider_ref`. It entitles the
// family because it is ACTIVE, not because anything anywhere checks `comp` —
// there is no `if (comp)` branch in the entitlement helper and there must never
// be one. That is precisely what keeps comps safe: a comp expires down exactly
// the same path as a paid subscription, with nothing to remember to undo.
//
// ⚠️ Granting to a family that already has a subscription REPLACES it. The API
// reuses the existing row so a family never holds two and `activeSubscription`
// stays unambiguous. The dialog says so out loud when that is what will happen.

import { Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConfirmDialog } from '../ConfirmDialog';
import { Icon } from '../Icon';
import { useToast } from '../Toaster';

import { listPlans } from '~/lib/billing';
import { CURRENCIES, type Currency } from '~/lib/money';
import { grantSubscription } from '~/lib/subscriptions';

export function AssignPlanDialog({
  open,
  onOpenChange,
  familyId,
  familyLabel,
  /** The family's current subscription status, if any. Drives the warning. */
  currentStatus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyId: string;
  familyLabel: string;
  currentStatus: string | null;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [planId, setPlanId] = useState('');
  const [currency, setCurrency] = useState<Currency>('QAR');
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [days, setDays] = useState('30');
  const [reason, setReason] = useState('');

  // Shares its query key with the Plans page, so opening this usually costs no
  // fetch at all.
  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: ({ signal }) => listPlans(signal),
    enabled: open,
  });

  // Only FAMILY plans can be granted here — the API rejects a school plan with
  // `invalid_input`, and schools are dark for MVP. Filtering client-side means
  // the operator is never offered something the server will refuse.
  const familyPlans = useMemo(
    () => (plans.data ?? []).filter((p) => p.audience === 'family'),
    [plans.data],
  );

  const dayCount = Number(days);
  const daysValid = Number.isInteger(dayCount) && dayCount > 0 && dayCount <= 3650;
  const valid = planId !== '' && daysValid && reason.trim() !== '';

  // Shown before confirming, so "30 days" is never left abstract.
  const endsOn = daysValid
    ? new Date(Date.now() + dayCount * 86_400_000).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

  const mutation = useMutation({
    mutationFn: () =>
      grantSubscription({
        familyId,
        planId,
        currency,
        interval,
        days: dayCount,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      // The family page, its invoice list, and both billing lists all now show
      // something different.
      await qc.invalidateQueries({ queryKey: ['family', familyId] });
      await qc.invalidateQueries({ queryKey: ['families'] });
      await qc.invalidateQueries({ queryKey: ['subscriptions'] });
      await qc.invalidateQueries({ queryKey: ['family-invoices', familyId] });
    },
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) {
          setPlanId('');
          setCurrency('QAR');
          setInterval('month');
          setDays('30');
          setReason('');
        }
      }}
      title="Assign a plan"
      description={`Gives ${familyLabel} paid-tier access without charging them. It becomes an ordinary active subscription and expires on its own — nothing has to be undone later.`}
      confirmLabel="Assign plan"
      tone="primary"
      disabled={!valid || mutation.isPending}
      onConfirm={async () => {
        await mutation.mutateAsync();
        toast(`Granted ${familyLabel} ${dayCount} day${dayCount === 1 ? '' : 's'} of access`, {
          tone: 'success',
        });
      }}
    >
      <div className="space-y-3">
        {/* The single most important thing to say before confirming: this is a
            replacement, not an addition. A family holds at most one row. */}
        {currentStatus && (
          <p className="flex items-start gap-1.5 rounded-lg bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700">
            <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>
              This family already has a <strong>{currentStatus}</strong> subscription. Assigning a
              plan REPLACES it — a family never holds two.
            </span>
          </p>
        )}

        <div>
          <label htmlFor="assign-plan" className="block text-sm font-medium text-admin-700">
            Plan
          </label>
          {plans.isError ? (
            // Never render an empty picker on a failed query — that is
            // indistinguishable from "there are no plans".
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-sm text-error-600">Could not load plans.</span>
              <button
                type="button"
                onClick={() => void plans.refetch()}
                className="text-sm font-semibold text-brand-600 underline"
              >
                Retry
              </button>
            </div>
          ) : (
            <select
              id="assign-plan"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              disabled={plans.isLoading}
              className="mt-1.5 h-10 w-full rounded-lg border border-admin-200 bg-white px-2.5 text-sm text-admin-900 disabled:opacity-50"
            >
              <option value="">Choose a plan…</option>
              {familyPlans.map((p) => (
                // slug, never nameKey — nameKey is a raw translation key.
                <option key={p.id} value={p.id}>
                  {p.slug}
                  {p.maxKids != null ? ` · up to ${p.maxKids} kids` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="assign-days" className="block text-sm font-medium text-admin-700">
              Length (days)
            </label>
            <Input
              id="assign-days"
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="mt-1.5"
              aria-invalid={days !== '' && !daysValid}
            />
            {endsOn ? (
              <p className="mt-1 text-xs text-admin-500">Access ends {endsOn}.</p>
            ) : (
              <p role="alert" className="mt-1 text-xs text-error-600">
                Between 1 and 3650 days.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="assign-interval" className="block text-sm font-medium text-admin-700">
              Billing shape
            </label>
            <select
              id="assign-interval"
              value={interval}
              onChange={(e) => setInterval(e.target.value as 'month' | 'year')}
              className="mt-1.5 h-10 w-full rounded-lg border border-admin-200 bg-white px-2.5 text-sm text-admin-900"
            >
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
            {/* Nothing is charged, so these two fields only describe the row.
                Saying so stops an operator agonising over a field that cannot
                cost anyone money. */}
            <p className="mt-1 text-xs text-admin-400">Recorded only — nothing is charged.</p>
          </div>
        </div>

        <div>
          <label htmlFor="assign-currency" className="block text-sm font-medium text-admin-700">
            Currency
          </label>
          <select
            id="assign-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            className="mt-1.5 h-10 w-full rounded-lg border border-admin-200 bg-white px-2.5 text-sm text-admin-900"
          >
            {CURRENCIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="assign-reason" className="block text-sm font-medium text-admin-700">
            Reason
          </label>
          <textarea
            id="assign-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Why is this being granted?"
            className="mt-1.5 w-full rounded-lg border border-admin-200 px-3 py-2 text-sm text-admin-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          <p className="mt-1 text-xs text-admin-400">Required. Stored in the audit log.</p>
        </div>
      </div>
    </ConfirmDialog>
  );
}
